import { ReactNode, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePlusIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row } from "@/components";

import { SheetResourcesControls } from "./SheetResourcesControls";
import { SheetResourcesWrapper } from "./SheetResourcesWrapper";
import { Certification } from "@/components/certifications/Certification";
import { CertificationsView } from "@/components/certifications/CertificationsView";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormNumberInput } from "@/components/form/FormNumberInput";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";

import { useCertification } from "@/hooks/mutations/useCertification";
import { monthOptions } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { CertificationType } from "@/types";

const certificationsTitle = "Certifications / Licenses";

const certificationSchema = z
  .object({
    name: z.string().min(1, { message: "Required" }),
    credentialId: z.string(),
    credentialUrl: z.string(),
    organization: z.string(),
    startMonth: z.string(),
    startYear: z.string().refine((data) => !isNaN(Number(data)), {
      message: "Year must be a valid integer",
    }),
    endMonth: z.string(),
    endYear: z.string().refine((data) => !isNaN(Number(data)), {
      message: "Year must be a valid integer",
    }),
  })
  .superRefine((data, ctx) => {
    dates.validateDatesForForm(data, ctx, true);
  });

const CertificationForm = ({
  form,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof certificationSchema>>>;
}) => {
  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4 [&_label]:font-plex-mono [&_label]:text-[11px] [&_label]:font-medium [&_label]:uppercase [&_label]:tracking-wider [&_label]:text-slate-400"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Row className="flex-wrap items-start gap-4">
          <FormTextInput
            className="min-w-40 flex-1"
            fieldName="name"
            form={form}
            label="Name"
          />
          <FormTextInput
            className="min-w-40 flex-1"
            fieldName="organization"
            form={form}
            label="Organization"
          />
        </Row>
        <Row className="flex-wrap items-start gap-4">
          <FormTextInput
            className="min-w-40 flex-1"
            fieldName="credentialId"
            form={form}
            label="Credential ID"
          />
          <FormTextInput
            className="min-w-40 flex-1"
            fieldName="credentialUrl"
            form={form}
            label="Credential URL"
          />
        </Row>
        <Row className="flex-wrap items-start gap-4">
          <FormEnumSelect
            className="min-w-40 flex-1"
            fieldName="startMonth"
            form={form}
            label="Issue Month"
            options={monthOptions}
            placeholder="Select month"
          />
          <FormNumberInput
            className="min-w-40 flex-1"
            fieldName="startYear"
            form={form}
            label="Issue Year"
          />
        </Row>
        <Row className="flex-wrap items-start gap-4">
          <FormEnumSelect
            className="min-w-40 flex-1"
            fieldName="endMonth"
            form={form}
            label="Expiration Month"
            options={monthOptions}
            placeholder="Select month"
          />
          <FormNumberInput
            className="min-w-40 flex-1"
            fieldName="endYear"
            form={form}
            label="Expiration Year"
          />
        </Row>
      </form>
    </Form>
  );
};

const CertificationEdit = ({
  certification,
  userId,
}: {
  certification: CertificationType;
  userId: number;
}) => {
  const [isShowingUpdateForm, setIsShowingUpdateForm] = useState(false);

  const { deleteCertification, updateCertification } = useCertification();

  const disabled =
    deleteCertification.isPending || updateCertification.isPending;

  const form = useForm<z.infer<typeof certificationSchema>>({
    defaultValues: {
      name: certification.name ?? "",
      organization: certification.organization ?? "",
      credentialId: certification.credentialId ?? "",
      credentialUrl: certification.credentialUrl ?? "",
      startMonth: certification.startMonth ?? "",
      startYear: certification.startYear ?? "",
      endMonth: certification.endMonth ?? "",
      endYear: certification.endYear ?? "",
    },
    mode: "onChange",
    resolver: zodResolver(certificationSchema),
  });

  // useCertification updates the sheet-data cache at the hook level, so
  // success handlers here only manage local form state.
  const handleDeleteCertification = () => {
    deleteCertification.mutate({ id: certification.id, userId });
  };

  const handleUpdateCertification = (
    data: z.infer<typeof certificationSchema>,
  ) => {
    const updateRequest = {
      id: certification.id,
      userId,
      name: data.name,
      organization: data.organization,
      credentialId: data.credentialId,
      credentialUrl: data.credentialUrl,
      startMonth: data.startMonth,
      startYear: data.startYear,
      endMonth: data.endMonth,
      endYear: data.endYear,
    };

    updateCertification.mutate(
      {
        userId: updateRequest.userId,
        data: updateRequest,
      },
      {
        onSuccess: (updatedCertification) => {
          handleUpdateCertificationSuccess(
            updatedCertification as CertificationType,
          );
        },
      },
    );
  };

  const handleUpdateCertificationSuccess = (
    updatedCertification: CertificationType,
  ) => {
    form.reset({
      name: updatedCertification.name ?? "",
      organization: updatedCertification.organization ?? "",
      credentialId: updatedCertification.credentialId ?? "",
      credentialUrl: updatedCertification.credentialUrl ?? "",
      startMonth: updatedCertification.startMonth ?? "",
      startYear: updatedCertification.startYear ?? "",
      endMonth: updatedCertification.endMonth ?? "",
      endYear: updatedCertification.endYear ?? "",
    });
    setIsShowingUpdateForm(false);
  };

  const handleCancel = () => {
    form.reset();
    setIsShowingUpdateForm(false);
  };

  const handleEdit = () => {
    setIsShowingUpdateForm(true);
  };

  const handleSave = () => {
    void form.handleSubmit(handleUpdateCertification)();
  };

  if (isShowingUpdateForm) {
    return (
      <Column className="gap-0">
        <Column className="w-full">
          <CertificationForm form={form} />
        </Column>
        <Row className="items-center justify-end pb-6 pt-4">
          <Button
            disabled={disabled || !form.formState.isValid}
            onClick={handleSave}
            size="sm"
            type="button"
          >
            Done
          </Button>
        </Row>
      </Column>
    );
  }

  return (
    <Row
      className="group/certification w-full items-start gap-2"
      onDoubleClick={handleEdit}
    >
      <Column className="min-w-0 flex-1">
        <Certification certification={certification} />
      </Column>
      <Column className="opacity-0 transition-opacity duration-150 group-hover/certification:opacity-100 group-hover/certification:delay-200">
        <SheetResourcesControls
          disabled={disabled}
          isEditing={false}
          onCancel={handleCancel}
          onDelete={handleDeleteCertification}
          onEdit={handleEdit}
          onSave={handleSave}
        />
      </Column>
    </Row>
  );
};

const CertificationAdd = ({
  handleCancel,
  userId,
}: {
  handleCancel: () => void;
  userId: number;
}) => {
  const { createCertification } = useCertification();

  const disabled = createCertification.isPending;

  const form = useForm<z.infer<typeof certificationSchema>>({
    defaultValues: {
      name: "",
      organization: "",
      credentialId: "",
      credentialUrl: "",
      startMonth: "",
      startYear: "",
      endMonth: "",
      endYear: "",
    },
    mode: "onChange",
    resolver: zodResolver(certificationSchema),
  });

  const handleCreateCertification = (
    data: z.infer<typeof certificationSchema>,
  ) => {
    const createRequest = {
      userId,
      name: data.name,
      organization: data.organization || undefined,
      credentialId: data.credentialId || undefined,
      credentialUrl: data.credentialUrl || undefined,
      startMonth: data.startMonth || undefined,
      startYear: data.startYear || undefined,
      endMonth: data.endMonth || undefined,
      endYear: data.endYear || undefined,
    };

    // useCertification updates the sheet-data cache at the hook level.
    createCertification.mutate(
      { userId, data: createRequest },
      {
        onSuccess: () => {
          form.reset();
          handleCancel();
        },
      },
    );
  };

  const handleSave = () => {
    void form.handleSubmit(handleCreateCertification)();
  };

  return (
    <Column className="items-end">
      <Button
        className="h-8 w-8"
        disabled={disabled}
        onClick={handleCancel}
        size="icon"
        type="button"
        variant="ghost"
      >
        <XIcon className="h-4 w-4 text-slate-600" />
      </Button>
      <Column className="w-full">
        <CertificationForm form={form} />
      </Column>
      <Row className="items-center justify-end pb-6 pt-4">
        <Button
          disabled={disabled || !form.formState.isValid}
          onClick={handleSave}
          size="sm"
          type="button"
        >
          Done
        </Button>
      </Row>
    </Column>
  );
};

const CertificationsEdit = ({
  certifications,
  isShowingAddForm,
  setIsShowingAddForm,
  userId,
}: {
  certifications: CertificationType[];
  isShowingAddForm: boolean;
  setIsShowingAddForm: (isShowing: boolean) => void;
  userId: number;
}) => {
  return (
    <Column className="gap-4">
      {isShowingAddForm ? (
        <CertificationAdd
          handleCancel={() => {
            setIsShowingAddForm(false);
          }}
          userId={userId}
        />
      ) : null}
      {certifications.map((certification) => (
        <CertificationEdit
          key={certification.id}
          certification={certification}
          userId={userId}
        />
      ))}
    </Column>
  );
};

export const SheetCertifications = ({
  certifications,
  titleActions,
  userId,
}: {
  certifications: CertificationType[];
  titleActions?: ReactNode;
  // Present when the profile is editable.
  userId?: number;
}) => {
  const [isShowingAddForm, setIsShowingAddForm] = useState(false);

  // Editable profiles get per-row edit/delete controls plus a header add
  // button — no section-wide edit mode.
  if (userId !== undefined) {
    return (
      <SheetResourcesWrapper
        isEmpty={!certifications.length}
        title={certificationsTitle}
        titleActions={
          isShowingAddForm ? undefined : (
            <Button
              className="h-6 w-6 opacity-0 transition-opacity duration-150 group-hover/section:opacity-100 group-hover/section:delay-200"
              onClick={() => {
                setIsShowingAddForm(true);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <CirclePlusIcon className="h-3.5 w-3.5 text-slate-500" />
            </Button>
          )
        }
      >
        <Column className="gap-4">
          {/* Hide the empty-state text while the add form is open. */}
          {!certifications.length && !isShowingAddForm ? (
            <CertificationsView certifications={certifications} />
          ) : null}
          <CertificationsEdit
            certifications={certifications}
            isShowingAddForm={isShowingAddForm}
            setIsShowingAddForm={setIsShowingAddForm}
            userId={userId}
          />
        </Column>
      </SheetResourcesWrapper>
    );
  }

  return (
    <SheetResourcesWrapper
      isEmpty={!certifications.length}
      title={certificationsTitle}
      titleActions={titleActions}
    >
      <CertificationsView certifications={certifications} />
    </SheetResourcesWrapper>
  );
};
